import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandL, BrandXXL } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DecisivePlayerPickerMode } from '@sorare/core/src/__generated__/globalTypes.ts';
import type { QuickplayNextLevelStory_taskInterface } from './__generated__/index.graphql.ts';
import bg from './assets/bg.webp';

const Root = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  height: 100%;
  width: 100%;
  padding: var(--double-unit);
  background: url(${bg});

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;
const Body = styled(Vertical).attrs({ gap: 5, center: true })`
  flex: 1;
`;

type Props = {
  onClose?: () => void;
  task: QuickplayNextLevelStory_taskInterface;
};
export const QuickplayNextLevelStory = ({ onClose, task }: Props) => {
  if (
    !isType(task, 'DecisivePlayerPickerTask') ||
    !isType(task.parentTask, 'TasksTrack')
  ) {
    return null;
  }

  const stepIndex = task.parentTask.steps.findIndex(({ id }) => id === task.id);
  const finalLevel = stepIndex === task.parentTask.steps.length - 1;

  const nextLevelIndex = stepIndex + 1;
  const nextLevelTask = task.parentTask.steps.at(nextLevelIndex);

  if (!isType(nextLevelTask, 'DecisivePlayerPickerTask')) {
    return null;
  }

  const { maxAppearancesCount, overperform, target, mode } = nextLevelTask;

  return (
    <Root>
      <Body>
        <Vertical gap={0} center>
          <img width={250} src={task.iconUrl || undefined} alt="" />
          <BrandXXL uppercase>
            {nextLevelTask ? (
              <FormattedMessage
                defaultMessage="Level {level}"
                values={{ level: nextLevelIndex + 1 }}
              />
            ) : (
              <FormattedMessage defaultMessage="Congrats!" />
            )}
          </BrandXXL>
        </Vertical>
        <Vertical center>
          <BrandL uppercase className="text-center">
            {nextLevelTask ? (
              <FormattedMessage defaultMessage="You’ve reached the next level!" />
            ) : (
              <FormattedMessage defaultMessage="You’ve completed the final level!" />
            )}
          </BrandL>
          <BodyM color="var(--c-nd-600)">
            {nextLevelTask ? (
              <FormattedMessage
                defaultMessage="{maxAppearancesCount, plural, one {# Target} other {# Targets}} (+{overperformBy})"
                values={{
                  maxAppearancesCount,
                  overperformBy:
                    mode === DecisivePlayerPickerMode.DECISIVE
                      ? target
                      : overperform?.by,
                }}
              />
            ) : (
              <FormattedMessage defaultMessage="Up for another fresh start?" />
            )}
          </BodyM>
        </Vertical>
      </Body>
      <Button
        size="medium"
        color={finalLevel ? 'secondary' : 'primary'}
        onClick={onClose}
        fullWidth
      >
        <FormattedMessage {...glossary.playNow} />
      </Button>
    </Root>
  );
};

QuickplayNextLevelStory.fragments = {
  taskInterface: gql`
    fragment QuickplayNextLevelStory_taskInterface on TaskInterface {
      id
      iconUrl
      parentTask {
        id
        ... on TasksTrack {
          id
          endDate
          steps {
            id
            ... on DecisivePlayerPickerTask {
              id
              maxAppearancesCount
              target
              mode
              overperform {
                by
              }
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<QuickplayNextLevelStory_taskInterface>,
};
